import { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Play, Pause, RotateCcw, Check } from 'lucide-react';
import { TextToSpeechService } from '@/services/integrations/elevenlabs/TextToSpeechService';
import { Voice } from '@/types/voice/voice';
import { toast } from 'sonner';

interface VoiceQuickTestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voice: Voice | null;
  defaultText?: string;
  onSelectVoice?: (voiceId: string) => void;
  showSelectButton?: boolean;
}

export const VoiceQuickTestSheet = ({
  open,
  onOpenChange,
  voice,
  defaultText = "Hey, what do you think about my voice? Do you think I could be one of your genies?",
  onSelectVoice,
  showSelectButton = false
}: VoiceQuickTestSheetProps) => {
  const [text, setText] = useState(defaultText);
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Reset state when voice changes or modal opens/closes
  useEffect(() => {
    if (open) {
      setAudioUrl(null);
      setIsPlaying(false);
      setText(defaultText);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [open, voice?.id, defaultText]);

  const handleGenerate = async () => {
    if (!voice || !text.trim()) return;
    setIsLoading(true);
    setAudioUrl(null);
    setIsPlaying(false);
    try {
      const url = await TextToSpeechService.synthesize({ voiceId: voice.id, text });
      setAudioUrl(url);
      // Auto-play the audio once generated
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    } catch (error) {
      toast.error('Failed to generate voice preview');
      console.error('TTS error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleRestart = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    setIsPlaying(true);
  };

  const handleSelectVoice = () => {
    if (voice && onSelectVoice) {
      onSelectVoice(voice.id);
      toast.success(`Voice selected: ${voice.name}`);
      onOpenChange(false);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl md:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Try {voice?.name || 'this voice'}</SheetTitle>
          <SheetDescription>
            Generate a custom sample to hear how this voice sounds
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4">
          <Textarea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your sample text..."
            className="resize-none"
          />
          <div className="space-y-3">
            <Button
              onClick={handleGenerate}
              disabled={!voice || isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              <span className="ml-2">Generate</span>
            </Button>

            {audioUrl && (
              <div className="relative overflow-hidden rounded-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10" />
                <div className="relative flex items-center gap-3 p-4 backdrop-blur-sm border border-white/20">
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    className="hidden"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={togglePlayPause}
                    className="shrink-0 bg-white/80 hover:bg-white border-white/20 transition-colors"
                  >
                    {isPlaying ? (
                      <Pause className="h-4 w-4 text-genie-primary" />
                    ) : (
                      <Play className="h-4 w-4 text-genie-primary" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={handleRestart}
                    className="shrink-0 bg-white/80 hover:bg-white border-white/20 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4 text-genie-primary" />
                  </Button>
                  <div className="flex-1 text-sm font-medium bg-gradient-to-r from-genie-primary to-genie-secondary bg-clip-text text-transparent">
                    {isPlaying ? 'Playing...' : 'Ready to play'}
                  </div>
                </div>
              </div>
            )}

            {showSelectButton && voice && (
              <Button
                onClick={handleSelectVoice}
                variant="default"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
              >
                <Check className="h-4 w-4 mr-2" />
                Use This Voice
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
